import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { TrendingUp, TrendingDown, ChevronDown, X, RefreshCw } from 'lucide-react';

const REFRESH_MS = 60 * 1000;

const TickerItem = ({ q }) => {
  const up = (q.change_pct || 0) >= 0;
  const color = up ? '#10B981' : '#EF4444';
  const ArrowIcon = up ? TrendingUp : TrendingDown;
  const isINR = q.unit && q.unit.startsWith('₹');
  return (
    <span className="mtb-item" data-testid={`ticker-${q.symbol}`}>
      <span className="mtb-name">{q.name}</span>
      <span className="mtb-price">
        {isINR ? '₹' : ''}{Number(q.price).toLocaleString(isINR ? 'en-IN' : 'en-US', { maximumFractionDigits: isINR ? 0 : 2 })}
      </span>
      <span className="mtb-change" style={{ color, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <ArrowIcon size={11} /> {up ? '+' : ''}{q.change_pct?.toFixed(2)}%
      </span>
    </span>
  );
};

const MarketTickerBar = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('market_bar_dismissed') === '1');
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/market/snapshot`, { timeout: 30000 });
      setData(r.data);
    } catch {
      // silent — keep last data
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (dismissed) return undefined;
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [dismissed]);

  if (dismissed) return null;

  // Show a slim loading bar while fetching first time (avoids the "ticker doesn't appear" feeling)
  if (!data) {
    return (
      <div data-testid="market-ticker-bar-loading" style={{ background: '#0F172A', color: '#94A3B8', height: 36, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '0.78rem', borderBottom: '1px solid #1E293B' }}>
        <span style={{ background: '#EF4444', color: 'white', padding: '3px 9px', borderRadius: 4, fontWeight: 800, letterSpacing: '0.08em', fontSize: '0.7rem', marginRight: 10 }}>LIVE MARKETS</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={11} style={{ animation: 'mtb-spin 1s linear infinite' }} /> Fetching NSE, BSE, Gold & Foreign markets…
        </span>
        <style>{`@keyframes mtb-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const indices = [
    ...(data.indian_indices || []),
    ...(data.indian_commodities || []),
    ...(data.foreign_indices || []),
    ...(data.commodities || []),
    ...(data.forex || []),
  ];

  return (
    <div data-testid="market-ticker-bar" className="mtb-wrap">
      {/* Scrolling ticker row */}
      <div className="mtb-row">
        <div className="mtb-label">LIVE MARKETS</div>
        <div className="mtb-scroll">
          <div className="mtb-track">
            {/* duplicate for seamless loop */}
            {[...indices, ...indices].map((q, i) => <TickerItem key={`${q.symbol}-${i}`} q={q} />)}
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="mtb-toggle"
          data-testid="market-bar-toggle"
          aria-label={expanded ? 'Hide top movers' : 'Show top movers'}
        >
          <span style={{ marginRight: 4 }}>Top Movers</span>
          <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
        </button>
        <button onClick={load} className="mtb-refresh" title="Refresh now" data-testid="market-bar-refresh" aria-label="Refresh">
          <RefreshCw size={12} style={{ animation: refreshing ? 'mtb-spin 0.8s linear infinite' : 'none' }} />
        </button>
        <button
          onClick={() => { setDismissed(true); sessionStorage.setItem('market_bar_dismissed', '1'); }}
          aria-label="Dismiss"
          className="mtb-close"
          data-testid="market-bar-close"
        >
          <X size={12} />
        </button>
      </div>

      {/* Expanded top movers panel */}
      {expanded && (
        <div className="mtb-panel" data-testid="market-panel">
          <div className="mtb-cols">
            <div className="mtb-col" data-testid="market-gainers">
              <h4 style={{ color: '#10B981' }}><TrendingUp size={14} /> Top 10 Gainers — NIFTY 50</h4>
              <table>
                <tbody>
                  {(data.top_gainers || []).map((q) => (
                    <tr key={q.symbol}>
                      <td style={{ fontWeight: 600 }}>{q.name}</td>
                      <td style={{ textAlign: 'right' }}>₹{Number(q.price).toLocaleString('en-IN')}</td>
                      <td style={{ textAlign: 'right', color: '#10B981', fontWeight: 700 }}>+{q.change_pct?.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mtb-col" data-testid="market-losers">
              <h4 style={{ color: '#EF4444' }}><TrendingDown size={14} /> Top 10 Losers — NIFTY 50</h4>
              <table>
                <tbody>
                  {(data.top_losers || []).map((q) => (
                    <tr key={q.symbol}>
                      <td style={{ fontWeight: 600 }}>{q.name}</td>
                      <td style={{ textAlign: 'right' }}>₹{Number(q.price).toLocaleString('en-IN')}</td>
                      <td style={{ textAlign: 'right', color: '#EF4444', fontWeight: 700 }}>{q.change_pct?.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mtb-foot">
            Data via Yahoo Finance • Updates every 60s • Last fetched: {data.fetched_at ? new Date(data.fetched_at).toLocaleTimeString('en-IN') : '—'}
          </div>
        </div>
      )}

      <style>{`
        .mtb-wrap { background: #0F172A; color: white; border-bottom: 1px solid #1E293B; }
        .mtb-row { display: flex; align-items: center; gap: 8px; padding: 0 10px; height: 36px; }
        .mtb-label {
          font-size: 0.7rem; font-weight: 800; letter-spacing: 0.08em;
          background: #EF4444; color: white; padding: 3px 9px; border-radius: 4px;
          flex-shrink: 0; animation: mtb-blink 1.6s ease-in-out infinite;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .mtb-label::before { content: ''; display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: white; }
        .mtb-scroll { flex: 1; overflow: hidden; white-space: nowrap; mask-image: linear-gradient(90deg, transparent, black 5%, black 95%, transparent); }
        .mtb-track {
          display: inline-flex; gap: 28px;
          animation: mtb-scroll 90s linear infinite;
          padding-left: 100%;
        }
        .mtb-item { display: inline-flex; align-items: center; gap: 7px; font-size: 0.78rem; }
        .mtb-name { color: #CBD5E1; font-weight: 700; }
        .mtb-price { color: #F1F5F9; font-weight: 600; }
        .mtb-change { font-weight: 700; }

        .mtb-toggle, .mtb-refresh, .mtb-close {
          background: rgba(255,255,255,0.08); color: white; border: 1px solid rgba(255,255,255,0.15);
          padding: 4px 9px; border-radius: 999px; font-size: 0.72rem; font-weight: 600;
          cursor: pointer; display: inline-flex; align-items: center; flex-shrink: 0;
        }
        .mtb-toggle:hover, .mtb-refresh:hover, .mtb-close:hover { background: rgba(255,255,255,0.16); }
        .mtb-refresh, .mtb-close { padding: 4px 7px; }

        .mtb-panel { background: #1E293B; padding: 14px 16px; }
        .mtb-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        .mtb-col { background: #0F172A; border-radius: 8px; padding: 10px 12px; }
        .mtb-col h4 { font-size: 0.78rem; margin: 0 0 8px; color: #F1F5F9; font-weight: 700; display: inline-flex; align-items: center; gap: 6px; }
        .mtb-col table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
        .mtb-col td { padding: 5px 6px; color: #E2E8F0; border-bottom: 1px solid rgba(255,255,255,0.05); }
        .mtb-foot { color: #94A3B8; font-size: 0.7rem; margin-top: 10px; text-align: center; }

        @keyframes mtb-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        @keyframes mtb-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes mtb-spin {
          to { transform: rotate(360deg); }
        }
        .mtb-scroll:hover .mtb-track { animation-play-state: paused; }

        @media (max-width: 720px) {
          .mtb-cols { grid-template-columns: 1fr; }
          .mtb-toggle span { display: none; }
          .mtb-label { font-size: 0.62rem; padding: 2px 6px; }
        }
      `}</style>
    </div>
  );
};

export default MarketTickerBar;
